import {
    constants,
    getConnectUser,
    getStorage,
    validationRules,
    VillageType
} from 'helpers'
import { findPermission } from 'helpers/findPermission'
import {
    lookupPrimaryPhone,
    PhoneLookupMatch
} from 'helpers/lookupPrimaryPhone'
import { sameNationalPhone } from 'helpers/phoneLookupMatch'
import {
    useAppSelector,
    useBindDispatch,
    useNetworkConnection,
    useValidation
} from 'hooks'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClientDetailsRouteContextType } from '../v3/types/routeContextType'
import { ContinueButton } from 'components/button/ContinueButton'
import { PhoneIcon } from 'assets'
import { colors } from 'styles/colors'
import { NumberInput, Spinner } from 'components'
import { PhoneMatchModal } from 'components/phoneMatch/PhoneMatchModal'
import { ClientType } from 'storeTypes'
import './phoneNumber.scss'

export const PhoneNumber: FC<ClientDetailsRouteContextType> = ({
    phoneNumberHandler,
    phoneNumberInputValue,
    continueButtonText,
    userCountry,
    handleSubmit,
    clientPhone,
    clientCode,
    isDetailsEdit,
    onOpenExistingClient,
    onPhoneLookupOutcome
}) => {
    const { searchResult } = useAppSelector((state) => state.clientSearch)
    const vesVillages = useAppSelector((state) => state.ves?.villages)
    const selectedVeVillages = useAppSelector(
        (state) => state.ve?.selectedVeVillages
    )
    const { isOnline } = useNetworkConnection()
    const { veVillages } = getConnectUser()
    const assignedVillageIds = useMemo(
        () =>
            [
                ...(veVillages || []).map(
                    (village: VillageType) => village.sf_id
                ),
                ...(vesVillages || []).map(
                    (village: VillageType) => village.sf_id
                ),
                ...(selectedVeVillages || []).map(
                    (village: VillageType) => village.sf_id
                )
            ].filter(Boolean),
        [veVillages, vesVillages, selectedVeVillages]
    )
    const canSeeDuplicatePhone = findPermission(
        getStorage(constants.USER_PERMISSIONS),
        constants.CAN_SEE_DUPLICATE_PHONE
    )
    const villageIds =
        veVillages?.map((village: VillageType) => village.sf_id) || []
    const { t } = useTranslation()
    const { clientSearchByPhoneHandler, clearClientSearchHandler } =
        useBindDispatch()
    const [isSearching, setIsSearching] = useState(false)
    const [lookupPending, setLookupPending] = useState(false)
    const [matches, setMatches] = useState<PhoneLookupMatch[]>([])
    const [showMatchModal, setShowMatchModal] = useState(false)
    const localMatchRef = useRef(false)
    const countryPhoneLengthConfig: Record<string, number> = {
        Mali: 8,
        Senegal: 9,
        "Cote D'Ivoire": 10
    }

    const phoneNumberValidationRule = validationRules({
        country: userCountry,
        phone: phoneNumberInputValue
    })

    const { errors, validateForm } = useValidation(
        { phone: phoneNumberInputValue },
        phoneNumberValidationRule
    )

    useEffect(() => {
        if (phoneNumberInputValue && phoneNumberInputValue.length > 0) {
            validateForm()
        }
    }, [phoneNumberInputValue, userCountry])

    const runLocalCheck = () => {
        if (!canSeeDuplicatePhone) {
            handleSubmit()
            return
        }
        if (!phoneNumberInputValue) return
        setIsSearching(true)
        clientSearchByPhoneHandler({
            phone: phoneNumberInputValue,
            villageIds
        })
    }

    const handlePhoneConfirmation = async () => {
        if (!phoneNumberInputValue || lookupPending) return
        if (
            clientCode &&
            sameNationalPhone(clientPhone, phoneNumberInputValue)
        ) {
            handleSubmit()
            return
        }
        if (!canSeeDuplicatePhone) {
            handleSubmit()
            return
        }
        setLookupPending(true)
        const result = await lookupPrimaryPhone({
            number: phoneNumberInputValue,
            excludeClientCode: clientCode,
            isOnline
        })
        setLookupPending(false)
        if (result?.status === 'match') {
            localMatchRef.current = false
            setMatches(result.clients)
            setShowMatchModal(true)
            return
        }
        if (result?.status === 'no_match') {
            onPhoneLookupOutcome?.(
                constants.ANALYTICS.NAMES.PHONE_LOOKUP_NO_MATCH
            )
        } else if (result?.status === 'offline') {
            onPhoneLookupOutcome?.(
                constants.ANALYTICS.NAMES.PHONE_LOOKUP_OFFLINE
            )
        }
        runLocalCheck()
    }

    useEffect(() => {
        return () => {
            clearClientSearchHandler()
        }
    }, [])

    useEffect(() => {
        if (!isSearching) return
        const localMatches = searchResult.filter(
            (client: ClientType) => client.client_code !== clientCode
        )
        if (
            localMatches.length === 0 ||
            clientPhone === phoneNumberInputValue
        ) {
            handleSubmit()
        } else {
            setMatches(
                localMatches.map((client: ClientType) => ({
                    ...client,
                    village:
                        (client as ClientType & { village_name?: string })
                            .village_name || client.village,
                    is_default: false
                }))
            )
            localMatchRef.current = true
            setShowMatchModal(true)
        }
        setIsSearching(false)
    }, [searchResult])

    return (
        <div>
            <p className="enrollment--input-label form-title">
                <PhoneIcon
                    fillColor={colors.black}
                    color={colors.black}
                    width="30"
                    height="30"
                />
                {t('enrollment.clientPhone')}
            </p>
            <div className="enrollment--input-phone enrollment-button">
                <div>
                    <NumberInput
                        length={
                            countryPhoneLengthConfig[userCountry || 'Senegal']
                        }
                        onChangeNumberHandler={(number) => {
                            phoneNumberHandler?.(number)
                        }}
                        className={`${
                            countryPhoneLengthConfig[userCountry || 0] > 7 &&
                            'enrollment--input-number'
                        }`}
                        initialValue={phoneNumberInputValue}
                    />
                    {errors.phone && (
                        <p className="enrollment--input-error">
                            {errors.phone}
                        </p>
                    )}
                </div>
                {phoneNumberInputValue && !errors.phone && (
                    <ContinueButton
                        onClick={handlePhoneConfirmation}
                        btnText={continueButtonText || t('continue')}
                        disable={lookupPending}
                    />
                )}
            </div>
            {lookupPending && <Spinner size="90" pageSpinner fullscreen />}
            <PhoneMatchModal
                show={showMatchModal}
                clients={matches}
                modification={isDetailsEdit}
                assignedVillageIds={assignedVillageIds}
                onOpen={async (client) => {
                    const opened = await onOpenExistingClient?.(
                        client,
                        localMatchRef.current ? 'local' : 'core'
                    )
                    if (opened) setShowMatchModal(false)
                }}
                onContinue={() => {
                    setShowMatchModal(false)
                    onPhoneLookupOutcome?.(
                        localMatchRef.current
                            ? constants.ANALYTICS.NAMES
                                  .PHONE_LOOKUP_CONTINUED_LOCAL
                            : constants.ANALYTICS.NAMES.PHONE_LOOKUP_CONTINUED
                    )
                    handleSubmit()
                }}
            />
        </div>
    )
}
